import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../cx';

/**
 * D-09 §15.11 — 앵커형 팝오버 (`.popover`, 자유 컨텐츠).
 *
 * 트리거(앵커) 요소 옆에 붙는 떠 있는 패널. document.body 에 포탈로 그려 어떤 overflow 조상에도 잘리지 않고,
 * 화면 가장자리에서는 안쪽으로 당기며 아래 공간이 모자라면 위로 뒤집는다. 쌓임은 `--z-dropdown`.
 * 바깥 클릭·Esc 로 닫는다 — 앵커 안 클릭은 바깥 클릭이 아니다(열고 닫는 토글은 트리거가 맡는다).
 * 목록형 드롭다운은 `.menu`(Select·Combobox)를 쓰고, 이 컴포넌트는 폼·달력 같은 자유 컨텐츠용이다.
 */
export interface PopoverProps {
  open: boolean;
  onClose: () => void;
  /** 팝오버가 붙는 요소(트리거) */
  anchorRef: React.RefObject<HTMLElement | null>;
  /** 폭(px). 없으면 내용 폭 */
  width?: number;
  /** 접근성 레이블(필수) */
  'aria-label': string;
  /** 앵커 왼쪽 끝(start) 또는 오른쪽 끝(end)에 맞춘다 */
  align?: 'start' | 'end';
  className?: string;
  children: React.ReactNode;
}

const GAP = 6;
const MARGIN = 8;

export function Popover({
  open,
  onClose,
  anchorRef,
  width,
  'aria-label': ariaLabel,
  align = 'start',
  className = '',
  children,
}: PopoverProps) {
  const popRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({ top: 0, left: 0, visibility: 'hidden' });

  // 앵커 기준 좌표 계산 — 먼저 숨긴 채 마운트해 크기를 잰 뒤 놓는다.
  const place = () => {
    const anchor = anchorRef.current;
    const pop = popRef.current;
    if (!anchor || !pop) return;
    const a = anchor.getBoundingClientRect();
    const w = pop.offsetWidth;
    const h = pop.offsetHeight;
    const maxH = window.innerHeight - MARGIN * 2;

    let left = align === 'end' ? a.right - w : a.left;
    left = Math.min(Math.max(left, MARGIN), Math.max(MARGIN, window.innerWidth - w - MARGIN));

    let top = a.bottom + GAP;
    if (top + h > window.innerHeight - MARGIN) {
      const above = a.top - GAP - h;
      top = above >= MARGIN ? above : Math.max(MARGIN, window.innerHeight - h - MARGIN);
    }
    // 화면보다 크면 팝오버 안에서 스크롤 — 내용이 들어갈 때는 overflow 를 건드리지 않아 안의 드롭다운이 잘리지 않는다.
    setStyle(h > maxH
      ? { top: MARGIN, left, maxHeight: maxH, overflowY: 'auto' }
      : { top, left });
  };

  useLayoutEffect(() => {
    if (!open) return;
    place();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, align, width]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!popRef.current?.contains(t) && !anchorRef.current?.contains(t)) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const onReflow = () => place();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    // 내용 높이가 바뀌어도(경고 줄 추가 등) 화면 밖으로 밀리지 않게 다시 놓는다
    const ro = typeof ResizeObserver !== 'undefined' && popRef.current ? new ResizeObserver(onReflow) : null;
    if (ro && popRef.current) ro.observe(popRef.current);
    return () => {
      ro?.disconnect();
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div
      ref={popRef}
      role="dialog"
      aria-label={ariaLabel}
      className={cx('popover', className)}
      style={{ ...style, ...(width ? { width } : {}) }}
    >
      {children}
    </div>,
    document.body,
  );
}
